import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { getImageUrl, joinPublicStorefrontUrl } from "../api/client";
import { useDispatch } from "react-redux";
import { addToCart } from "../Features/Cart/cartSlice";
import Seo from "../Components/Seo/Seo";
import { useSiteSeo } from "../context/SiteSeoContext";
import { usePageSeo } from "../hooks/usePageSeo";
import { useManagedPageContent } from "../hooks/useManagedPageContent";
import BlockRenderer from "../Components/BlockRenderer/BlockRenderer";
import { usePrerenderReady } from "../utils/prerenderReady";
import ContextHeroSlider from "../Components/Hero/ContextHeroSlider";
import toast from "react-hot-toast";
import { FaCartPlus, FaHeart, FaFire, FaLeaf, FaStar } from "react-icons/fa";
import { buildOccasionFacets as buildCatalogFacets, filterOccasionProducts as filterCatalogProducts } from "./occasionFilters";
import "./Occasions.css";
import "./FestiveVibes.css";

function ProductCard({ product }) {
  const dispatch = useDispatch();
  const imageUrl = getImageUrl(
    product.first_image ||
      (Array.isArray(product.images) && product.images[0]?.url) ||
      (Array.isArray(product.images) && product.images[0]) ||
      null
  );
  const price = product.min_price_paise != null
    ? `₹${(product.min_price_paise / 100).toLocaleString("en-IN")}`
    : null;

  const handleAddToCart = (e) => {
    e.preventDefault();
    dispatch(addToCart({
      productId: product.id,
      productName: product.name,
      productPrice: product.min_price_paise ? product.min_price_paise / 100 : 0,
      productImage: imageUrl,
      quantity: 1,
    }));
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <Link to={`/product/${product.id}`} className="fv-card">
      <div className="fv-card-img-wrap">
        {imageUrl ? (
          <img src={imageUrl} alt={product.name} className="fv-card-img" loading="lazy" />
        ) : (
          <div className="fv-card-img-placeholder" />
        )}
        <button
          className="fv-card-cart-btn"
          onClick={handleAddToCart}
          aria-label={`Add ${product.name} to cart`}
        >
          <FaCartPlus />
        </button>
      </div>
      <div className="fv-card-info">
        <p className="fv-card-name">{product.name}</p>
        {price && <p className="fv-card-price">From {price}</p>}
      </div>
    </Link>
  );
}

export default function FestiveVibes() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: "", color: "", size: "", maxPrice: null });
  const [categorySearch, setCategorySearch] = useState("");
  // Active festival schedule: determines which hero slider to show
  const [heroContextKey, setHeroContextKey] = useState("Festive vibes");
  const [activeFestival, setActiveFestival] = useState(null);
  const siteCtx = useSiteSeo();
  const orgName = siteCtx.organization_name?.trim() || "Gem Fashion Studios";
  const { seo_title: pageSeoTitle, seo_description: pageSeoDesc, og_image_url: pageOgImage } = usePageSeo('festive-vibes');
  const { blocks, loaded: contentLoaded } = useManagedPageContent('festive-vibes');

  usePrerenderReady(!loading && contentLoaded);

  // Fetch active festival schedule to determine hero slider
  useEffect(() => {
    fetch(joinPublicStorefrontUrl("/api/festival-schedule/active"))
      .then((r) => r.ok ? r.json() : { active: null })
      .then((data) => {
        if (data.active && data.active.context_key) {
          setHeroContextKey(data.active.context_key);
          setActiveFestival(data.active);
        } else {
          setHeroContextKey("Festive vibes");
          setActiveFestival(null);
        }
      })
      .catch(() => {
        setHeroContextKey("Festive vibes");
        setActiveFestival(null);
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(joinPublicStorefrontUrl("/api/products?category=Festive+vibes"))
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const facets = useMemo(() => buildCatalogFacets(products), [products]);
  const visibleCategories = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    return query ? facets.categories.filter(({ name }) => name.toLowerCase().includes(query)) : facets.categories;
  }, [facets.categories, categorySearch]);

  const displayed = useMemo(() => filterCatalogProducts(products, filters), [products, filters]);
  const setFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value }));

  return (
    <>
      <Seo
        title={pageSeoTitle || `Festive Vibes — Celebration Outfits | ${orgName}`}
        description={pageSeoDesc || "Dress to dazzle at every celebration. Discover our curated festive collection — shimmering lehengas, embroidered sarees, and statement kurtas for weddings, Diwali, and every joyful occasion."}
        canonicalPath="/festive-vibes"
        ogImage={pageOgImage || undefined}
      />

      {/* Hero slider — auto-switches to active festival's slider when a schedule is live */}
      <ContextHeroSlider context="shop" contextKey={heroContextKey} />

      {/* Festival banner — shown when a scheduled festival is active */}
      {activeFestival && (
        <div className="fv-festival-banner">
          <FaStar className="fv-festival-banner-icon" />
          <span>
            Currently celebrating: <strong>{activeFestival.name}</strong>
            {activeFestival.description ? ` — ${activeFestival.description}` : ""}
          </span>
        </div>
      )}

      {blocks ? <BlockRenderer content={blocks} /> : <section className="fv-highlights">
        <div className="fv-highlight-item">
          <FaHeart className="fv-highlight-icon" />
          <span>Weddings &amp; Sangeets</span>
        </div>
        <div className="fv-highlight-item">
          <FaFire className="fv-highlight-icon" />
          <span>Diwali &amp; Navratri</span>
        </div>
        <div className="fv-highlight-item">
          <FaLeaf className="fv-highlight-icon" />
          <span>Mehendi &amp; Haldi</span>
        </div>
        <div className="fv-highlight-item">
          <FaStar className="fv-highlight-icon" />
          <span>Festival Gatherings</span>
        </div>
      </section>}

      {/* Product grid */}
      <section className="fv-grid-section">
        {loading ? (
          <div className="fv-skeleton-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="fv-skeleton-card" />
            ))}
          </div>
        ) : (
          <div className="occ-catalog-layout">
            <aside className="occ-filter" aria-label="Filter festive outfits">
              <div className="occ-filter-topline">
                <h2>Product filters</h2>
                {Object.values(filters).some((value) => value !== "" && value != null) && (
                  <button type="button" onClick={() => setFilters({ category: "", color: "", size: "", maxPrice: null })}>Clear</button>
                )}
              </div>
              <details open>
                <summary>Product categories</summary>
                <input type="search" value={categorySearch} onChange={(event) => setCategorySearch(event.target.value)} placeholder="Search categories" aria-label="Search categories" />
                <div className="occ-filter-options">
                  {visibleCategories.map(({ name, count }) => (
                    <button type="button" key={name} className={filters.category === name ? "active" : ""} onClick={() => setFilter("category", filters.category === name ? "" : name)}>
                      <span>{name}</span><small>({count})</small>
                    </button>
                  ))}
                </div>
              </details>
              <details>
                <summary>Color</summary>
                <div className="occ-filter-options">
                  {facets.colors.map(({ name, count }) => (
                    <button type="button" key={name} className={filters.color === name ? "active" : ""} onClick={() => setFilter("color", filters.color === name ? "" : name)}>
                      <span>{name}</span><small>({count})</small>
                    </button>
                  ))}
                </div>
              </details>
              <details>
                <summary>Sizes</summary>
                <div className="occ-filter-size-options">
                  {facets.sizes.map(({ name }) => (
                    <button type="button" key={name} className={filters.size === name ? "active" : ""} onClick={() => setFilter("size", filters.size === name ? "" : name)}>{name}</button>
                  ))}
                </div>
              </details>
              <details>
                <summary>Price (from / day)</summary>
                <input type="range" min={facets.minPrice} max={facets.maxPrice || 1} value={filters.maxPrice ?? facets.maxPrice} onChange={(event) => setFilter("maxPrice", Number(event.target.value))} aria-label="Maximum daily rental price" />
                <p className="occ-filter-price">Up to ₹{Math.round(filters.maxPrice ?? facets.maxPrice).toLocaleString("en-IN")}</p>
              </details>
            </aside>
            <div className="occ-results">
              <p className="occ-results-count">{displayed.length} outfit{displayed.length === 1 ? "" : "s"}</p>
              {displayed.length === 0 ? (
                <div className="fv-empty"><p>No festive styles match these filters. <button type="button" onClick={() => setFilters({ category: "", color: "", size: "", maxPrice: null })}>Clear filters</button></p></div>
              ) : (
                <div className="fv-grid">{displayed.map((p) => <ProductCard key={p.id} product={p} />)}</div>
              )}
            </div>
          </div>
        )}
      </section>

      {!blocks && <section className="fv-bottom-cta">
        <h2>Can't find what you're looking for?</h2>
        <p>Explore our full catalog or reach out — we'll help you find the perfect outfit for your celebration.</p>
        <div className="fv-bottom-cta-btns">
          <Link to="/shop" className="fv-cta-btn primary">Browse all styles</Link>
          <Link to="/contact" className="fv-cta-btn secondary">Contact us</Link>
        </div>
      </section>}
    </>
  );
}
